import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { getInstance, listTabs } from '@/api/real-browsers';
import VncViewer from '@/components/VncViewer';
import ActionPanel from '@/components/ActionPanel';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * The headline page. Two-column layout on desktop, stacked on mobile.
 *
 *   left:  noVNC viewer (live pixel stream — full mouse + keyboard)
 *   right: ActionPanel (CDP one-shots — screenshot, navigate, click, …)
 *   below: live tab list polled every 3 s from CDP /json/list
 */
export default function Live() {
  const { instanceId } = useParams({ from: '/protected/live/$instanceId' });

  const { data: instance, isLoading: instLoading } = useQuery({
    queryKey: ['instance', instanceId],
    queryFn: () => getInstance(instanceId),
    refetchInterval: 5_000,
  });

  const { data: tabs = [], isError: tabsError } = useQuery({
    queryKey: ['instance', instanceId, 'tabs'],
    queryFn: () => listTabs(instanceId),
    refetchInterval: 3_000,
  });

  if (instLoading) {
    return <div className="text-sm text-slate-500">loading…</div>;
  }
  if (!instance) {
    return <div className="text-sm text-slate-500">instance not found.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link
          to="/"
          className="tap-target rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition"
          aria-label="Back to instances"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="min-w-0">
          <h1 className="text-xl font-semibold truncate">{instance.id}</h1>
          <p className="text-xs text-slate-500">
            {instance.cdp_host}:{instance.cdp_port} · {instance.status} ·
            last seen {relativeTime(instance.last_heartbeat_at)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <VncViewer instanceId={instanceId} />
        </div>
        <div className="lg:col-span-1">
          <ActionPanel instanceId={instanceId} />
        </div>
      </div>

      <div className="bg-slate-900/60 border border-slate-800 rounded-xl">
        <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-sm font-medium">Open tabs</h2>
          <span className="text-[11px] text-slate-500">live · CDP /json/list</span>
        </div>
        {tabsError ? (
          <div className="p-4 text-xs text-red-300">CDP unreachable.</div>
        ) : tabs.length === 0 ? (
          <div className="p-4 text-xs text-slate-500">No open tabs.</div>
        ) : (
          <div className="divide-y divide-slate-800">
            {tabs.map(t => (
              <div key={t.id} className="p-3 flex items-start gap-3">
                <span className={cn(
                  'mt-1 dot shrink-0',
                  t.type === 'page' ? 'bg-sky-500' : 'bg-slate-500',
                )} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate" title={t.title}>{t.title || '(untitled)'}</div>
                  <div className="text-[11px] text-slate-500 font-mono truncate">
                    {t.type} · {t.url}
                  </div>
                </div>
                {t.url?.startsWith('http') && (
                  <a
                    href={t.url}
                    target="_blank" rel="noreferrer"
                    className="text-slate-500 hover:text-white shrink-0"
                    aria-label="Open in new tab"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
